import { functions } from "../firebase";

// Client wrapper for the one admin-gated `mediaOps` callable
// (functions/src/mediaOpsCallable.ts). The server runs the same operation
// registry the MCP facades use, so the UI holds no business rules: it sends
// {communityId, op, input} and renders the envelope it gets back.
//
// Types below are local, tolerant copies of mcp/src/mediaOps/types.ts. The
// admin build cannot import from mcp/ or functions/. Unknown fields are kept.

export const MEDIA_OPS_CALLABLE = "mediaOps";
export const MEDIA_OPS_TIMEOUT_MS = 120_000;

export type DeliveryMode = "owner_email" | "tiktok_inbox" | "auto_publish";
export type ScheduleState =
  | "unscheduled"
  | "scheduled"
  | "dispatching"
  | "completed"
  | "cancelled"
  | "cancellation_requested"
  | "blocked";
export type DeliveryState = "pending" | "accepted" | "processing" | "handed_off" | "failed" | "unknown";
export type PublicationState = "unconfirmed" | "owner_reported" | "provider_verified" | "failed";
export type RenderState = "queued" | "running" | "succeeded" | "failed" | "cancelled";
export type OperationStatus = "completed" | "queued" | "partial" | "blocked";

export const PLATFORMS = ["instagram", "facebook", "tiktok", "youtube", "linkedin", "x", "threads"] as const;
export const DELIVERY_MODES: DeliveryMode[] = ["owner_email", "tiktok_inbox", "auto_publish"];
export const MEDIA_FORMATS = [
  "text_post",
  "slideshow",
  "wall_of_text",
  "founder_clip",
  "hook_demo",
  "influencer_photo",
  "influencer_video",
  "green_screen",
  "single_image",
  "video",
] as const;
export const POLICY_TEMPLATES = ["founderInstagram", "founderTiktok", "influencer", "brand"] as const;
export const IDENTITY_KINDS = ["founder", "influencer", "brand"] as const;
export const SOURCE_POLICIES = ["founder_owned_only", "identity_owned", "brand_any_permitted"] as const;

export type OpWarning = { code: string; message: string };
export type OpError = {
  code: string;
  message: string;
  retryable: boolean;
  accountId?: string;
  slotId?: string;
  requiredAction?: string;
  details?: Record<string, unknown>;
};

export type OperationResult<T = any> = {
  operationId: string;
  status: OperationStatus;
  data: T;
  warnings: OpWarning[];
  errors: OpError[];
  /**
   * Client only. True when the call never produced a server envelope
   * (network, timeout, unavailable). The same idempotency key is safe and
   * correct to reuse for a retry.
   */
  transportError?: boolean;
};

export type MediaOpsTransport = (payload: {
  communityId: string;
  op: string;
  input: Record<string, unknown>;
}) => Promise<unknown>;

const defaultTransport: MediaOpsTransport = async (payload) => {
  const callable = functions.httpsCallable(MEDIA_OPS_CALLABLE, { timeout: MEDIA_OPS_TIMEOUT_MS });
  const res = await callable(payload);
  return res.data;
};

let transport: MediaOpsTransport = defaultTransport;

/** Tests swap the transport. Production never calls this. */
export const setMediaOpsTransport = (t: MediaOpsTransport | null): void => {
  transport = t || defaultTransport;
};

// Functions HttpsError codes that mean the request may not have reached the
// server, or its answer was lost. Retrying with the same key is safe.
const TRANSPORT_CODES = new Set([
  "unavailable",
  "deadline-exceeded",
  "internal",
  "unknown",
  "cancelled",
  "resource-exhausted",
]);

const HTTPS_CODE_MAP: Record<string, string> = {
  "permission-denied": "FORBIDDEN",
  unauthenticated: "UNAUTHENTICATED",
  "invalid-argument": "VALIDATION_FAILED",
  "failed-precondition": "FAILED_PRECONDITION",
  "not-found": "NOT_FOUND",
  "already-exists": "CONFLICT",
  aborted: "VERSION_CONFLICT",
};

const isEnvelope = (v: unknown): v is OperationResult =>
  !!v &&
  typeof v === "object" &&
  typeof (v as any).status === "string" &&
  Array.isArray((v as any).errors);

const normalizeEnvelope = <T>(v: OperationResult): OperationResult<T> => ({
  operationId: typeof v.operationId === "string" ? v.operationId : "",
  status: v.status,
  data: (v.data ?? null) as T,
  warnings: Array.isArray(v.warnings) ? v.warnings : [],
  errors: Array.isArray(v.errors) ? v.errors : [],
});

/** Turns any thrown value from the callable into an envelope-like result. */
export const normalizeCallError = <T = any>(err: unknown): OperationResult<T> => {
  const e = (err || {}) as { code?: string; message?: string; details?: unknown };
  if (isEnvelope(e.details)) return normalizeEnvelope<T>(e.details);
  const raw = typeof e.code === "string" ? e.code.replace(/^functions\//, "") : "";
  const message = typeof e.message === "string" && e.message ? e.message : "The request failed.";
  const policyBlocked = /POLICY_BLOCKED/.test(message);
  const transportError = !raw || TRANSPORT_CODES.has(raw);
  const code = policyBlocked
    ? "POLICY_BLOCKED"
    : HTTPS_CODE_MAP[raw] || (transportError ? "NETWORK_ERROR" : raw.toUpperCase().replace(/-/g, "_"));
  return {
    operationId: "",
    status: "blocked",
    data: null as unknown as T,
    warnings: [],
    errors: [
      {
        code,
        message:
          code === "FORBIDDEN"
            ? `You do not have permission for this action in this community. ${message}`
            : transportError
            ? `${message} Nothing is known to have changed. You can retry safely.`
            : message,
        retryable: transportError,
      },
    ],
    transportError,
  };
};

/** Calls one operation. Always resolves with an envelope, never throws. */
export const callMediaOps = async <T = any>(
  communityId: string,
  op: string,
  input: Record<string, unknown> = {}
): Promise<OperationResult<T>> => {
  try {
    const data = await transport({ communityId, op, input });
    if (isEnvelope(data)) return normalizeEnvelope<T>(data);
    return {
      operationId: "",
      status: "blocked",
      data: null as unknown as T,
      warnings: [],
      errors: [{ code: "BAD_RESPONSE", message: "The server returned an unexpected response.", retryable: false }],
    };
  } catch (err) {
    return normalizeCallError<T>(err);
  }
};

// Idempotency keys. One key per user intent. Reuse it when a retry follows a
// transport error (the first call may have landed). Mint a fresh one after any
// server answer, or when the user edits the form.

const hex = (bytes: Uint8Array) => Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");

/** RFC 4122 v4 uuid from crypto.getRandomValues. */
export const newIdempotencyKey = (): string => {
  const bytes = new Uint8Array(16);
  const c: Crypto | undefined = typeof globalThis !== "undefined" ? (globalThis as any).crypto : undefined;
  if (c && typeof c.getRandomValues === "function") {
    c.getRandomValues(bytes);
  } else {
    for (let i = 0; i < 16; i++) bytes[i] = Math.floor(Math.random() * 256);
  }
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const h = hex(bytes);
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
};

export class IntentKey {
  private key: string | null = null;

  /** The key for the current intent, minted on first use. */
  current(): string {
    if (!this.key) this.key = newIdempotencyKey();
    return this.key;
  }

  /** The user changed the form: the next submit is a new intent. */
  reset(): void {
    this.key = null;
  }

  /** Keep the key only when the server never answered. */
  settle(result: Pick<OperationResult, "transportError">): void {
    if (!result.transportError) this.key = null;
  }
}

/** Runs a mutation under an intent key and settles the key from the result. */
export const runMutation = async <T = any>(
  intent: IntentKey,
  communityId: string,
  op: string,
  input: Record<string, unknown>
): Promise<OperationResult<T>> => {
  const res = await callMediaOps<T>(communityId, op, { ...input, idempotencyKey: intent.current() });
  intent.settle(res);
  return res;
};

// Read helpers. Mutations go through runMutation so they carry a key.

export const opRead = <T = any>(communityId: string, op: string, input: Record<string, unknown> = {}) =>
  callMediaOps<T>(communityId, op, input);

export const isOk = (r: OperationResult): boolean => r.status === "completed" || r.status === "queued";

export const hasError = (r: OperationResult, code: string): boolean => r.errors.some((e) => e.code === code);

// Loose shapes the views read. Every field is optional on purpose: an older or
// newer server must never crash the page.

export type AccountSummary = {
  id: string;
  platform: string;
  handle: string;
  identityId: string;
  providerAccountId?: string | null;
  timezone: string;
  status: string;
  allowedModes: DeliveryMode[];
  defaultMode: DeliveryMode | null;
  formats?: string[];
  prohibitedFormats?: string[];
  sourcePolicy?: string | null;
  policyVersion: number;
  ownerConfigured: boolean;
  ownerMasked: string | null;
  ownerVersion: number;
  verification?: { status: string; evidence?: string; checkedAt?: string };
  conflicts?: Array<{ communityId?: string; accountId?: string; claimedByOtherCommunity?: boolean }>;
  version: number;
  [k: string]: unknown;
};

export type IdentitySummary = {
  id: string;
  kind: string;
  name: string;
  voice?: string;
  audience?: string;
  version: number;
  [k: string]: unknown;
};

export type AssetSummary = {
  id: string;
  sourceKind: string;
  subjectIdentityId: string;
  status: "eligible" | "quarantined" | "rejected" | string;
  type: string;
  width?: number | null;
  height?: number | null;
  durationSec?: number | null;
  tags?: string[];
  ancestorIds?: string[];
  mediaAssetId?: string | null;
  storagePath?: string | null;
  reasons?: string[];
  reuse?: { count: number; lastUsedAt?: string; byAccount?: Record<string, number> };
  [k: string]: unknown;
};

export type TargetSummary = {
  targetId: string;
  scheduleId: string;
  accountId: string;
  handle: string | null;
  platform: string | null;
  deliveryMode: DeliveryMode;
  approvalId: string;
  revisionId: string;
  states: { schedule: ScheduleState; delivery: DeliveryState; publication: PublicationState };
  scheduleState: ScheduleState;
  intendedPublishAt: string;
  dispatchAt: string;
  timezone: string;
  attemptCount: number;
  lastError: OpError | null;
  handoffId: string | null;
  retryEligible?: boolean;
  cancellationNote?: string | null;
  publicationEvidence?: { publishedAt?: string; publishedUrl?: string; source?: string; actor?: { kind?: string } } | null;
  attempts?: Array<{
    attemptId: string;
    phase: string;
    outcome?: string | null;
    provider: string;
    createdAt: string;
    updatedAt?: string;
    error?: OpError | null;
  }>;
  handoff?: { handoffId: string; emailStatus?: string | null; publication?: { state: string } } | null;
  version?: number;
  [k: string]: unknown;
};

export type DeliverySummary = {
  scheduleId: string;
  projectId: string;
  revisionId: string;
  campaignId?: string | null;
  version: number;
  targets: TargetSummary[];
  createdAt?: string;
  [k: string]: unknown;
};

export type HandoffMedia = {
  fileName: string;
  type: string;
  order: number;
  sizeBytes?: number;
  downloadUrl?: string | null;
  /** ISO time the signed downloadUrl stops working. */
  expiresAt?: string | null;
  storagePath?: string;
  [k: string]: unknown;
};

/** Who reported a publication, as stored with the evidence. */
export type PublicationSource = "owner" | "admin" | "mcp" | "script" | "provider";

export type PublicationEvidenceView = {
  state: string;
  publishedAt?: string;
  publishedUrl?: string;
  reportedAt?: string;
  source?: PublicationSource | string;
  actor?: { kind?: string; label?: string };
};

export type OwnerHandoffView = {
  handoffId: string;
  scheduleId: string;
  targetId: string;
  accountId: string;
  handle: string;
  timezone: string;
  intendedPublishAt: string;
  manifest: { caption: string; media: HandoffMedia[] };
  captionFile?: { fileName: string; downloadUrl?: string | null; expiresAt?: string | null } | null;
  emailStatus?: string | null;
  publication?: PublicationEvidenceView;
  [k: string]: unknown;
};

export type ProjectRow = {
  projectId: string;
  title: string | null;
  format: string;
  identityId: string;
  currentRevisionId: string;
  review?: { status: string; reviewId: string | null; version: number | null };
  targets: { accountId: string; deliveryMode: string }[];
  conceptId?: string | null;
  campaignSlotId?: string | null;
  version: number;
  updatedAt?: string;
  [k: string]: unknown;
};

export type CampaignRow = {
  campaignId: string;
  briefId: string;
  briefVersion?: number;
  identityId?: string | null;
  startDate: string;
  endDate: string;
  rerollsUsed?: number;
  rerollAllowance?: number;
  coverage?: { slots: number; withRevision: number; byStatus: Record<string, number>; gapsByReason: Record<string, number>; truncated?: boolean };
  version: number;
  createdAt?: string;
  [k: string]: unknown;
};

export type ReviewMedia = {
  order: number;
  assetId?: string | null;
  storagePath?: string | null;
  type?: string | null;
  role?: string;
  mediaUrl?: string | null;
  thumbnailUrl?: string | null;
  [k: string]: unknown;
};

export type ReviewTarget = {
  accountId: string;
  handle: string | null;
  platform: string | null;
  deliveryMode: string;
  caption: string | null;
  ownerMasked: string | null;
  timezone: string | null;
  allowed?: boolean;
  [k: string]: unknown;
};

export type ReviewView = {
  reviewId: string;
  projectId: string;
  revisionId: string;
  status: string;
  version: number;
  format?: string | null;
  identityId?: string | null;
  media: ReviewMedia[];
  captionsByAccount?: Record<string, string>;
  targets: ReviewTarget[];
  findings?: Record<string, { code: string; message: string; requiredAction?: string }[]>;
  approvals?: Record<string, string>;
  feedback?: { message: string; at: string }[];
  [k: string]: unknown;
};

export type JobSummary = {
  jobId: string;
  type: string;
  status: RenderState;
  stage: string;
  progress: number;
  attempt?: number;
  budget?: { limitUsd?: number; reservedUsd?: number; actualUsd?: number; unsettledUsd?: number };
  diagnostics?: { result?: string; sources?: Record<string, any>[]; targets?: Record<string, any>[]; failed?: Record<string, any>[] } | null;
  error?: OpError | null;
  cancelRequested?: boolean;
  projectId?: string | null;
  resultRevisionId?: string | null;
  version: number;
  createdAt?: string;
  [k: string]: unknown;
};

export const TERMINAL_JOB_STATES: RenderState[] = ["succeeded", "failed", "cancelled"];
export const isTerminalJob = (s: string | undefined): boolean => TERMINAL_JOB_STATES.includes(s as RenderState);

// Per-op helpers. Reads take no key. Mutations take an IntentKey.

export const mediaOps = {
  // Accounts and identities.
  listAccounts: (cid: string, input: Record<string, unknown> = {}) =>
    opRead<{ accounts: AccountSummary[]; nextCursor: string | null }>(cid, "list_social_accounts", { limit: 100, ...input }),
  getAccountPolicy: (cid: string, accountId: string) => opRead(cid, "get_social_account_policy", { accountId }),
  listIdentities: (cid: string) =>
    opRead<{ identities: IdentitySummary[]; nextCursor: string | null }>(cid, "list_social_identities", { limit: 100 }),
  checkDestinationConflicts: (cid: string) => opRead(cid, "check_destination_conflicts", {}),
  upsertAccount: (k: IntentKey, cid: string, input: Record<string, unknown>) => runMutation(k, cid, "upsert_social_account", input),
  setOwner: (k: IntentKey, cid: string, input: { accountId: string; expectedVersion: number; email: string; reason?: string }) =>
    runMutation(k, cid, "set_social_account_owner", input),
  setVerification: (k: IntentKey, cid: string, input: Record<string, unknown>) => runMutation(k, cid, "set_account_verification", input),
  updatePolicy: (k: IntentKey, cid: string, input: Record<string, unknown>) => runMutation(k, cid, "update_social_account_policy", input),
  upsertIdentity: (k: IntentKey, cid: string, input: Record<string, unknown>) => runMutation(k, cid, "upsert_social_identity", input),

  // Assets.
  listAssets: (cid: string, input: Record<string, unknown> = {}) =>
    opRead<{
      assets: AssetSummary[];
      nextCursor: string | null;
      eligibleAssetIds?: string[];
      exclusions?: { assetId: string; reasons: { code: string; message: string }[] }[];
    }>(cid, "list_media_assets", { limit: 100, ...input }),
  registerOwned: (k: IntentKey, cid: string, input: Record<string, unknown>) => runMutation(k, cid, "register_owned_media", input),
  importAsset: (k: IntentKey, cid: string, input: Record<string, unknown>) => runMutation(k, cid, "import_media_asset", input),
  attestAsset: (k: IntentKey, cid: string, input: Record<string, unknown>) => runMutation(k, cid, "attest_asset_ownership", input),

  // Projects and reviews.
  listProjects: (cid: string, input: Record<string, unknown> = {}) =>
    opRead<{ projects: ProjectRow[]; nextCursor: string | null }>(cid, "list_media_projects", { limit: 50, ...input }),
  getReview: (cid: string, reviewId: string) => opRead<ReviewView>(cid, "get_media_review", { reviewId }),
  createProject: (k: IntentKey, cid: string, input: Record<string, unknown>) => runMutation(k, cid, "create_media_project", input),
  reviseProject: (k: IntentKey, cid: string, input: Record<string, unknown>) => runMutation(k, cid, "revise_media_project", input),
  getProject: (cid: string, projectId: string, revisionId?: string) =>
    opRead(cid, "get_media_project", revisionId ? { projectId, revisionId } : { projectId }),
  submitReview: (k: IntentKey, cid: string, input: Record<string, unknown>) => runMutation(k, cid, "submit_media_for_review", input),
  approve: (k: IntentKey, cid: string, input: Record<string, unknown>) => runMutation(k, cid, "approve_media_revision", input),
  requestChanges: (k: IntentKey, cid: string, input: Record<string, unknown>) => runMutation(k, cid, "request_media_changes", input),

  // Jobs.
  startRender: (k: IntentKey, cid: string, input: Record<string, unknown>) => runMutation(k, cid, "start_media_render", input),
  getJob: (cid: string, jobId: string) => opRead<JobSummary>(cid, "get_media_job", { jobId }),
  listJobs: (cid: string, input: Record<string, unknown> = {}) =>
    opRead<{ jobs: JobSummary[]; nextCursor: string | null }>(cid, "list_media_jobs", { limit: 50, ...input }),
  cancelJob: (k: IntentKey, cid: string, input: { jobId: string; expectedVersion: number }) => runMutation(k, cid, "cancel_media_job", input),

  // Delivery.
  schedule: (k: IntentKey, cid: string, input: Record<string, unknown>) => runMutation(k, cid, "schedule_media_delivery", input),
  listDeliveries: (cid: string, input: Record<string, unknown> = {}) =>
    opRead<{ deliveries: DeliverySummary[]; nextCursor: string | null }>(cid, "list_media_deliveries", { limit: 50, ...input }),
  getDelivery: (cid: string, scheduleId: string) => opRead<DeliverySummary>(cid, "get_media_delivery", { scheduleId }),
  cancelDelivery: (k: IntentKey, cid: string, input: { scheduleId: string; expectedVersion: number; targetIds?: string[] }) =>
    runMutation(k, cid, "cancel_media_delivery", input),
  retryDelivery: (k: IntentKey, cid: string, input: { scheduleId: string; expectedVersion: number; targetIds?: string[] }) =>
    runMutation(k, cid, "retry_media_delivery", input),
  reconcileDelivery: (k: IntentKey, cid: string, input: { scheduleId: string; targetIds?: string[] }) =>
    runMutation(k, cid, "reconcile_media_delivery", input),

  // Owner handoff.
  previewHandoff: (cid: string, scheduleId: string, targetId: string) => opRead(cid, "preview_owner_handoff", { scheduleId, targetId }),
  getHandoff: (cid: string, handoffId: string) => opRead<OwnerHandoffView>(cid, "get_owner_handoff", { handoffId }),
  reissueHandoff: (k: IntentKey, cid: string, handoffId: string) => runMutation(k, cid, "reissue_owner_handoff_access", { handoffId }),
  reportPublication: (k: IntentKey, cid: string, input: { handoffId: string; publishedAt: string; publishedUrl?: string }) =>
    runMutation(k, cid, "report_manual_publication", input),
  setPublicationUrl: (k: IntentKey, cid: string, input: { scheduleId: string; targetId: string; publishedAt: string; publishedUrl: string }) =>
    runMutation(k, cid, "report_manual_publication", input),

  // Briefs, campaigns, trends, health.
  listCampaigns: (cid: string, input: Record<string, unknown> = {}) =>
    opRead<{ campaigns: CampaignRow[]; nextCursor: string | null }>(cid, "list_social_campaigns", { limit: 30, ...input }),
  saveBrief: (k: IntentKey, cid: string, input: Record<string, unknown>) => runMutation(k, cid, "save_social_brief", input),
  createCampaign: (k: IntentKey, cid: string, input: Record<string, unknown>) => runMutation(k, cid, "create_social_campaign", input),
  listSlots: (cid: string, campaignId: string, input: Record<string, unknown> = {}) =>
    opRead(cid, "list_campaign_slots", { campaignId, limit: 200, ...input }),
  generateSlots: (k: IntentKey, cid: string, input: Record<string, unknown>) => runMutation(k, cid, "generate_campaign_slots", input),
  rerollSlot: (k: IntentKey, cid: string, input: Record<string, unknown>) => runMutation(k, cid, "reroll_campaign_slot", input),
  listTrends: (cid: string, input: Record<string, unknown> = {}) => opRead(cid, "list_trend_evidence", { limit: 100, ...input }),
  saveTrend: (k: IntentKey, cid: string, input: Record<string, unknown>) => runMutation(k, cid, "save_trend_evidence", input),
  startTrendScan: (k: IntentKey, cid: string, input: Record<string, unknown>) => runMutation(k, cid, "start_trend_scan", input),
  refreshPostMetrics: (k: IntentKey, cid: string, input: Record<string, unknown>) => runMutation(k, cid, "refresh_post_metrics", input),
  socialInsights: (cid: string) => opRead(cid, "get_social_learning_report", { windowDays: 28 }),
  health: (cid: string, input: Record<string, unknown> = {}) => opRead(cid, "get_media_operations_health", input),
};

export default mediaOps;
